import { Stack, Typography, useMediaQuery } from '@mui/material'
import { FunctionComponent } from 'react'
import gaiaTheme from '../../../root-components/theme'
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode'
import { GraphBarChart } from '../../../components/presentation-components/analytics-components/graphs/graph-bar-chart'
import { SeriesBar } from '../../../shared/interfaces/analytics'
import {
  getGaiaReportSectionShellSx,
  getGaiaReportBodyTextSx,
} from '../../public-reports/components/gaia-report-web-page-pdf-styles'
import type { IColorPalatte } from '../shared-services/project-reports.model'
import { useReportContentOverride } from '../../public-reports/shared-services/gaia-report-content'

function normaliseBarData(raw: any): { label: string; totalEmission: number }[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  return raw.breakdown ?? raw.marketBased ?? raw.locationBased ?? []
}

export const ProjectReportWebPageCategories: FunctionComponent<{
  colorPalatte: IColorPalatte
  categoryBarData: any
  projectLabel: string
}> = ({ colorPalatte, categoryBarData, projectLabel }) => {
  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'))
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'))
  const pdfExport = usePdfExportMode()

  const barItems = normaliseBarData(categoryBarData)

  const heading = useReportContentOverride('emissionsByCategory.heading')
  const body = useReportContentOverride('emissionsByCategory.body')

  return (
    <Stack sx={{
      ...getGaiaReportSectionShellSx({ backgroundColor: colorPalatte.dark, pdfExport }),
      ...(pdfExport ? {} : { mt: -5, position: 'relative', zIndex: 3 }),
    }}>
      <Typography variant={isMobileModeSm ? 'h5' : 'h4'} sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
        {heading ?? 'Emissions by Category'}
      </Typography>
      <Typography sx={{ ...getGaiaReportBodyTextSx({ color: 'rgba(255,255,255,0.8)', isMobileModeMd, isMobileModeSm, pdfExport }), mb: 2 }}>
        {body ?? `Breakdown of this ${projectLabel.toLowerCase()}'s total emissions across each emission category.`}
      </Typography>
      {barItems.length > 0 ? (
        <div className='chart-block'>
          <GraphBarChart
            title='Emissions by Category'
            dataset={barItems}
            dataKey='label'
            colors={[colorPalatte.light]}
            series={SeriesBar}
            textColor='white'
            layout='vertical'
            backgroundColor='transparent'
            chartHeight={pdfExport ? 260 : 400}
            paperPadding={pdfExport ? 2 : 3}
            titleVariant={pdfExport ? 'body2' : 'subtitle1'}
          />
        </div>
      ) : (
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', mt: 2 }}>
          No category data available.
        </Typography>
      )}
    </Stack>
  )
}
